import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Reveal } from '../components/Reveal';
import { CursorGlow } from '../components/CursorGlow';
import { CertificateModal } from '../components/CertificateModal';
import { ReadingProgress } from '../components/ReadingProgress';
import { SplitText } from '../components/SplitText';
import { HeroPortraitMorph } from '../components/HeroPortraitMorph';
import { useParallax } from '../hooks/useParallax';
import { useMagnetic } from '../hooks/useMagnetic';
import { useTilt } from '../hooks/useTilt';
import { BookOpen, Briefcase, Music, Mic, Building2, Dumbbell, Award, Users, FileText, Target, Mail, Linkedin, Instagram, ExternalLink, ChevronDown, GraduationCap, Trophy, Compass, BookMarked, Activity, Mountain, Gem } from 'lucide-react';

// Animation timings (ms)
const NAV_SCROLL_THRESHOLD_PX = 50;
const STAGGER_AWARDS_MS = 50;
const STAGGER_CERT_MS = 60;
const STAGGER_DOMAINS_MS = 70;
const STAGGER_ACHIEVEMENT_MS = 80;
const STAGGER_PILLAR_MS = 90;
const STAGGER_PROJECT_MS = 100;
const STAGGER_MANIFESTO_MS = 120;
const CREDENTIAL_STEP_MS = 80;

const ProjectMedia = ({ project }) => {
  if (project.video) {
    return (
      <div className="project-image-wrapper project-video-wrapper">
        <video
          src={project.video}
          poster={project.poster}
          className="project-image project-video"
          controls
          preload="metadata"
          playsInline
          style={project.videoPosition ? { objectPosition: project.videoPosition } : undefined}
        />
      </div>
    );
  }
  if (project.gallery) {
    return (
      <div className="project-image-wrapper project-gallery-wrapper">
        {project.gallery.map((img, gi) => (
          <div key={img.src} className="project-gallery-item">
            <img
              src={img.src}
              alt={`${project.title} ${gi + 1}`}
              className="project-image"
              style={img.position ? { objectPosition: img.position } : undefined}
            />
          </div>
        ))}
      </div>
    );
  }
  if (project.image) {
    return (
      <div className={`project-image-wrapper ${project.imageFit === 'contain' ? 'project-image-contain' : ''}`}>
        <img
          src={project.image}
          alt={project.title}
          className="project-image"
          style={{
            ...(project.imagePosition ? { objectPosition: project.imagePosition } : {}),
            ...(project.imageFit ? { objectFit: project.imageFit } : {})
          }}
        />
      </div>
    );
  }
  return null;
};

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeCert, setActiveCert] = useState(null);
  useParallax();
  useMagnetic();
  useTilt({ maxTilt: 5, scale: 1.012 });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > NAV_SCROLL_THRESHOLD_PX);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // ---- Cinematic hero: all animation driven by a single rAF loop, no React state per frame ----
  const heroRef = useRef(null);
  const portraitRef = useRef(null);
  const auraRef = useRef(null);
  const copyRef = useRef(null);
  const captionRef = useRef(null);
  const captionTextRef = useRef(null);
  const numeralRef = useRef(null);
  const morphRef = useRef(null); // exposes imgs(), veil(), hairline()

  const heroPersonas = [
    {
      src: 'https://customer-assets-cm19k8pv.emergentagent.net/job_strateg-jha/artifacts/hs1i5f7j_IMG_8643.PNG',
      position: 'center 43%',
      caption: '',
      numeral: ''
    },
    {
      src: 'https://customer-assets-cm19k8pv.emergentagent.net/job_strateg-jha/artifacts/o2inxz6f_DSC_0280.webp',
      position: 'center 35%',
      caption: 'MUSIC',
      numeral: 'I'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/ob0d33xn_DSCN1557.JPG',
      position: 'center 30%',
      caption: 'BUSINESS',
      numeral: 'II'
    },
    {
      src: 'https://customer-assets-cm19k8pv.emergentagent.net/job_strateg-jha/artifacts/44wh5ptg_IMG_3219.webp',
      position: 'center 30%',
      caption: 'ORATORY',
      numeral: 'III'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/6wmbu9tj_CAMT4864.JPG',
      position: 'center 30%',
      caption: 'FAMILY',
      numeral: 'IV'
    }
  ];

  useEffect(() => {
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let rafId = null;
    let lastCaptionIdx = -1;

    const update = () => {
      rafId = null;
      const hero = heroRef.current;
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const total = hero.offsetHeight - window.innerHeight;
      if (total <= 0) return;
      const progress = clamp(-rect.top / total, 0, 1);

      // Rectangular plate scale — computed dynamically so it always fully covers
      // the viewport regardless of aspect ratio. Eased for smoothness.
      const frameEl = portraitRef.current?.querySelector('.portrait-frame');
      const scaleProgress = easeInOutSine(clamp(progress / 0.92, 0, 1));
      let maxScale = 1.6;
      if (frameEl) {
        const fw = frameEl.offsetWidth;
        const fh = frameEl.offsetHeight;
        if (fw > 0 && fh > 0) {
          // Contain-fit: the plate grows large but never full-bleed, so faces stay a respectful distance.
          maxScale = Math.min(window.innerWidth / fw, window.innerHeight / fh) * 0.92;
        }
      }
      const scale = 1 + scaleProgress * (maxScale - 1);
      if (portraitRef.current) {
        portraitRef.current.style.transform = `translate3d(0,0,0) scale(${scale.toFixed(4)})`;
      }

      // Aura
      const auraP = easeOutCubic(clamp(progress / 0.6, 0, 1));
      if (auraRef.current) {
        auraRef.current.style.opacity = auraP.toFixed(3);
        auraRef.current.style.transform = `translate(-50%, -50%) scale(${(1 + auraP * 1.6).toFixed(3)})`;
      }

      // Copy fade + rise
      const copyP = clamp(progress / 0.18, 0, 1);
      const copyOpacity = 1 - easeOutCubic(copyP);
      if (copyRef.current) {
        copyRef.current.style.opacity = copyOpacity.toFixed(3);
        copyRef.current.style.transform = `translate3d(0, ${(-progress * 60).toFixed(1)}px, 0)`;
        copyRef.current.style.pointerEvents = copyOpacity < 0.03 ? 'none' : 'auto';
      }

      // Caption container fade — hidden entirely on personas without a caption (opening headshot)
      const capP = clamp((progress - 0.05) / 0.12, 0, 1);
      const currentIdx = clamp(Math.round(progress * (heroPersonas.length - 1)), 0, heroPersonas.length - 1);
      if (captionRef.current) {
        const visible = heroPersonas[currentIdx].caption ? 1 : 0;
        captionRef.current.style.opacity = (easeOutCubic(capP) * visible).toFixed(3);
      }

      // Persona cross-fades — LINEAR keeps opacities summing to 1 at any midpoint
      const lastIdx = heroPersonas.length - 1;
      const position = progress * lastIdx;
      const imgs = morphRef.current?.imgs() || [];
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        if (!img) continue;
        const d = Math.abs(position - i);
        // Widened overlap keeps the plate bright mid-crossfade (no "empty" moment)
        const o = clamp((1 - d) * 1.7, 0, 1);
        img.style.opacity = o.toFixed(3);
        // Small zoom-in on incoming image for depth
        const z = 1 + (1 - o) * 0.05;
        img.style.transform = `scale(${z.toFixed(3)})`;
      }

      // Cinematic veil pulses at peak of each cross-fade
      const veil = morphRef.current?.veil();
      if (veil) {
        const frac = position - Math.floor(position);
        const peak = 4 * frac * (1 - frac);
        veil.style.opacity = (peak * 0.22).toFixed(3);
      }

      // The plate no longer goes full-bleed, so keep the antique-gold hairline as a persistent frame.
      const hairline = morphRef.current?.hairline?.();
      if (hairline) {
        hairline.style.opacity = '1';
      }

      // Caption text + numeral — only mutate DOM when the segment changes
      const captionIdx = clamp(Math.round(position), 0, lastIdx);
      if (captionIdx !== lastCaptionIdx) {
        lastCaptionIdx = captionIdx;
        const persona = heroPersonas[captionIdx];
        if (captionTextRef.current) captionTextRef.current.textContent = persona.caption;
        if (numeralRef.current) numeralRef.current.textContent = persona.numeral;
        // Re-trigger the caption swap animation
        if (captionRef.current) {
          captionRef.current.classList.remove('caption-swap');
          // force reflow to restart animation
          void captionRef.current.offsetWidth;
          captionRef.current.classList.add('caption-swap');
        }
      }
    };

    const onScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    if (reduce) {
      // Set a static, elegant end-state without any motion driving
      if (portraitRef.current) portraitRef.current.style.transform = '';
      update();
    } else {
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const achievements = [
  {
    title: 'Book & Translation Work',
    description: 'Translated Cry No More Sita and saw it through publication, with the chance to present the work to the Honourable Vice President of Nepal.',
    icon: BookOpen,
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/r7tm0s06_C1C9D3E5-C9FC-4637-BAC7-C9A54FC9D928.jpg',
    imagePosition: 'center 25%'
  },
  {
    title: 'Research Paper Publication',
    description: 'Published research on the impact of Big Data on statistical analysis methods in the Journal of Advances in Science and Technology.',
    icon: FileText
  },
  {
    title: 'PurdueThink Consulting Project',
    description: 'Consulted for Purdue\'s campus planning team on a wayfinding and navigation project, improving how students move across one of America\'s largest university campuses.',
    icon: Target,
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/ob0d33xn_DSCN1557.JPG',
    imagePosition: 'center 30%'
  },
  {
    title: 'Vimarsh Literature Festival',
    description: 'Helped organise the Vimarsh Literature Festival in Kathmandu, the largest Maithili literature conference held in Nepal to date.',
    icon: Users,
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/o5vq3w89_Screenshot%20%28423%29.png',
    imagePosition: 'center 25%'
  }];


  const passions = [
  { title: 'Business & Institution Building', icon: Briefcase },
  { title: 'Literature & Language', icon: BookOpen },
  { title: 'Hindustani Music', icon: Music },
  { title: 'Rhetoric & Public Discourse', icon: Mic },
  { title: 'Real Estate & Luxury Hospitality', icon: Building2 },
  { title: 'Fitness & Self-Mastery', icon: Dumbbell }];


  const projects = [
  {
    title: 'Digital Marketing & Strategy Design for Nepovit',
    description: 'A brand, marketing, and creative strategy engagement for Nepovit, one of Nepal\'s premium tile companies. Designed the visual identity of campaigns across social media, conceived and produced the marketing materials that were distributed across platforms, and worked alongside the leadership on the broader marketing plan for the brand. The work moved between two registers throughout: the creative discipline of building images that hold attention in a saturated feed, and the strategic discipline of deciding what the brand should say, to whom, and in what order.',
    type: 'Brand · Marketing Strategy · Creative Direction',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/20hw5h20_Nepovit%20Tiles%20Advanced%20Branding%20.png',
    imagePosition: 'center center'
  },
  {
    title: 'Director of Operations, Ascend Purdue',
    description: 'Served as Director of Operations for Ascend Purdue, overseeing fundraising and the club\'s programme of events across the academic year.',
    type: 'Leadership',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_strateg-jha/artifacts/qxmp0r8j_Screenshot%202026-09-18%20at%2018.55.39.png',
    imageFit: 'contain',
    imagePosition: 'center center'
  },
  {
    title: 'Ascend Purdue — Goody\'s Popcorn Repositioning',
    description: 'A brand repositioning project undertaken with Ascend Purdue for Goody\'s Popcorn, working through market research, brand strategy, and recommendations for repositioning.',
    type: 'Brand Strategy · Consulting',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/yhblte4x_Screenshot%202026-06-21%20at%2000.47.00.png',
    imageFit: 'contain',
    imagePosition: 'center center'
  },
  {
    title: 'Hindustani Music Coordinator — ICMAP',
    description: 'Music Coordinator at the Indian Classical Music Association at Purdue. Organised and managed Hindustani and Indian classical music events, balancing performance with the work behind it.',
    type: 'Music · Events',
    gallery: [
      { src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/v0cjhn7d_IMG_0559.JPG', position: 'center 30%' },
      { src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/tn0xfttw_DSC_0280.webp', position: 'center 15%' }
    ]
  },
  {
    title: 'Cry No More Sita',
    description: 'A major literary and translation project that allowed me to engage closely with contemporary Nepali literature. Presented to the Honourable Vice President of Nepal, with a public launch in Kathmandu hosted by the Vimarsh Foundation.',
    type: 'Translation · Literature',
    gallery: [
      { src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/2eyi1ssi_d5dac4d3-488b-4df6-a5c1-576bf4b9e997.jpg', position: 'center 15%' },
      { src: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/r7tm0s06_C1C9D3E5-C9FC-4637-BAC7-C9A54FC9D928.jpg', position: 'center 25%' }
    ]
  },
  {
    title: 'Nepal Television Broadcast',
    description: 'Featured on Nepal Television, the country\'s largest television channel, during the launch and release of the translated book. The conversation traced the arc of the translation, the original Maithili text, and the wider intent behind carrying a regional literary work to a national stage.',
    type: 'Media · Broadcast',
    video: '/media/nepal-tv.mp4#t=7',
    poster: '/media/nepal-tv-poster.jpg',
    videoPosition: 'center top'
  },
  {
    title: 'Radio Interview',
    description: 'Hosted and interviewed on Nepal\'s largest radio network, in conversation about the translation of Cry No More Sita and the wider literary work behind it.',
    type: 'Media',
    video: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/ia10k2eu_6f5d5584-d25a-445a-9ebd-7a1f28823171.mp4'
  },
  {
    title: 'Editorial Journalism',
    description: 'Published as Associate Writer — Technology at The Rising Nepal, and editor across The Sankalp Magazine, The Oliphant, and The Wavelength at Welham Boys\' School.',
    type: 'Writing · Editorial',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/tjz8ys25_Screenshot%202026-06-13%20at%2014.29.01.png',
    imageFit: 'contain',
    imagePosition: 'center center',
    link: 'https://risingnepaldaily.com/news/28183'
  },
  {
    title: 'School Podcast',
    description: 'Hosted a podcast series during my time at Welham, exploring student perspectives and conversations. The series crossed 25,000 views on Instagram.',
    type: 'Media',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/lelr6l4i_IMG_1805.jpg',
    imageFit: 'contain',
    imagePosition: 'center center',
    link: 'https://youtu.be/9oPugOjxXH0?si=29hPs6O9YzhxysCT'
  },
  {
    title: 'Orchestra Direction',
    description: 'Directed The Ensemble, North India\'s largest student orchestral performance, leading a company of one hundred and twenty-eight musicians across strings, winds, percussion, and Hindustani classical instruments. The work shaped much of how I think about ensemble, rhythm, and the discipline of holding many voices inside a single intention.',
    type: 'Music · Direction',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/79al3n6n_Screenshot%202026-06-16%20at%2021.57.29.png',
    imagePosition: 'center 30%'
  },
  {
    title: 'Debating',
    description: 'Represented the school across several inter-school debate circuits, including first position at the Wynberg Allen School Debate and Speaker of the Year at the Welham Girls\' Saroj Srivastava Debates.',
    type: 'Public Speaking',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/6jjnti9j_Screenshot%20%28417%29.png',
    imagePosition: 'center 25%'
  },
  {
    title: 'Family Business & Stewardship',
    description: 'Time spent learning from the family business and the people who keep it running. Pictured during a small gesture of thanks to the factory team on my twentieth birthday.',
    type: 'Stewardship',
    image: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/gks1soga_Screenshot%202026-06-12%20at%2016.15.34.png',
    imagePosition: 'center 30%'
  }];


  const polymathPillars = [
  {
    title: 'Excellence in One\'s Field',
    description: 'Mastery matters. A man should pursue serious competence and distinction in his chosen work.',
    icon: Compass
  },
  {
    title: 'Culture & The Arts',
    description: 'Literature, music, history, architecture, and beauty should shape his mind and enrich his life.',
    icon: BookMarked
  },
  {
    title: 'Gentlemanship',
    description: 'Manners, grace, self-restraint, respect, and presence are not superficial. They are marks of civilisation.',
    icon: Award
  },
  {
    title: 'Physical Discipline',
    description: 'The body should reflect effort, self-command, and vitality.',
    icon: Activity
  },
  {
    title: 'Character & Composure',
    description: 'Strength is not loudness. It is steadiness, dignity, and integrity under pressure.',
    icon: Mountain
  },
  {
    title: 'Taste & Aesthetic Refinement',
    description: 'A cultivated man develops discernment in clothing, speech, surroundings, and judgement.',
    icon: Gem
  }];


  const polymathManifesto = [
  'Success without culture becomes hollow.',
  'Culture without discipline becomes decorative.',
  'Manners are not weakness. They are civilisation.',
  'A man should know how to build, speak, host, appreciate, and lead.',
  'The aim is not performance. The aim is refinement of the whole self.'];


  const polymathMoodboard = [
  { src: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&q=80', alt: 'Library and books' },
  { src: 'https://images.unsplash.com/photo-1564399579883-451a5d44ec08?w=600&q=80', alt: 'Classical architecture' },
  { src: 'https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=600&q=80', alt: 'Piano keys' },
  { src: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&q=80', alt: 'Writing with fountain pen' }];


  const certifications = [
  {
    title: 'Strategy Consulting',
    issuer: 'BCG',
    link: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/rs9564dl_Screenshot%202026-06-16%20at%2022.54.34.png'
  },
  {
    title: 'Introduction to Strategy Consulting',
    issuer: 'BCG',
    link: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/33pq9jlv_Screenshot%202026-06-16%20at%2022.54.15.png'
  },
  {
    title: 'Introduction to Data for Decision Makers',
    issuer: 'BCG',
    link: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/zk9mp25a_Screenshot%202026-06-16%20at%2022.54.25.png'
  },
  {
    title: 'Operations Job Simulation',
    issuer: 'Goldman Sachs',
    link: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/j2o6sw79_Screenshot%202026-06-16%20at%2022.54.03.png'
  },
  {
    title: 'Financial Accounting: Accounting Cycle Simternship',
    issuer: 'Stukent',
    link: 'https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/3z9bieqv_Screenshot%202026-06-16%20at%2022.53.53.png'
  }];


  const awards = [
  { title: 'School Subject Award for Music', issuer: 'Welham Boys\' School', date: '2024' },
  { title: 'Faculty Award for Best Behaviour', issuer: 'Welham Boys\' School', date: 'Feb 2024' },
  { title: 'Gentleman of the Year Award', issuer: 'Welham Boys\' School', date: 'Feb 2024' },
  { title: 'Speaker of the Year Award', issuer: 'Welham Boys\' School', date: 'Feb 2024' },
  { title: 'Vocalist of the Year Award', issuer: 'Welham Boys\' School', date: 'Apr 2023' },
  { title: 'First Position, Inter School Debate', issuer: 'Wynberg Allen School', date: 'May 2023' },
  { title: 'Best Delegate, MUN', issuer: 'Mayo College Girls School, Ajmer', date: 'Apr 2023' },
  { title: 'Speaker of the Year, Saroj Srivastava Debates', issuer: 'Welham Girls\' School', date: 'Apr 2023' },
  { title: 'First Position, Inter School Band Competition', issuer: 'Shri Ram Centennial School' }];


  return (
    <div className="portfolio-container">
      <CursorGlow />
      <ReadingProgress />
      <CertificateModal cert={activeCert} onClose={() => setActiveCert(null)} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="portfolio-logo">BJ</div>
          <div className="hidden md:flex gap-8">
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
            <button onClick={() => scrollToSection('work')} className="nav-link">Work</button>
            <button onClick={() => scrollToSection('polymath')} className="nav-link">Philosophy</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section — cinematic sticky sequence, animated via rAF DOM writes */}
      <section className="hero-section hero-cinematic" ref={heroRef}>
        <div className="hero-sticky">
          <div ref={auraRef} className="hero-aura" aria-hidden="true" />
          <div ref={portraitRef} className="hero-portrait mb-8 hero-portrait-cinematic">
            <div className="portrait-frame">
              <HeroPortraitMorph ref={morphRef} personas={heroPersonas} />
            </div>
          </div>
          <div ref={copyRef} className="hero-copy">
            <h1 className="hero-title"><SplitText text="Bimarsh Jha" stagger={55} startDelay={150} /></h1>
            <h2 className="hero-subtitle">Guided by Project Polymath </h2>
            <p className="hero-supporting">
              A student of industry, language, and culture. Reading Purdue's Industrial Engineering Technology programme while working inside Nepovit Group.
            </p>
            <div className="flex flex-wrap gap-4 mt-8 justify-center">
              <Button onClick={() => scrollToSection('work')} className="cta-primary magnetic" size="lg">
                View My Work
              </Button>
              <Button onClick={() => scrollToSection('about')} variant="outline" className="cta-secondary magnetic" size="lg">
                Explore My Story
              </Button>
              <Button onClick={() => scrollToSection('contact')} variant="outline" className="cta-secondary magnetic" size="lg">
                Contact Me
              </Button>
            </div>
          </div>
          <div ref={captionRef} className="hero-caption" aria-live="polite">
            <span ref={numeralRef} className="hero-caption-numeral"></span>
            <div className="hero-caption-rule">
              <span className="hero-caption-line" />
              <span ref={captionTextRef} className="hero-caption-text"></span>
              <span className="hero-caption-line" />
            </div>
          </div>
          <button onClick={() => scrollToSection('about')} className="scroll-indicator">
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-light">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">About</h2></Reveal>
          <div className="about-grid mt-12">
            <Reveal variant="fade-right" className="about-image-wrapper">
              <img
                src="https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/uyy1dw58_545C0DD5-7EE2-47FB-BADF-66C2887FF3DC.jpg"
                alt="Bimarsh at Kedarkantha peak"
                className="about-image"
              />
              <p className="about-image-caption">Kedarkantha peak, 12,500 ft.</p>
            </Reveal>
            <Reveal variant="fade-left" delay={150}>
              <div className="prose-custom">
                <p>
                  I come from a business family in Nepal, and that has shaped how I think about almost everything. Growing up alongside Nepovit Group, a publicly listed construction materials company built by my father, I came to understand early that building something of consequence requires more than a good idea: it requires systems, people, discipline, and patience.
                </p>
                <p>
                  That instinct drove me toward Industrial Engineering Technology at Purdue University, and back to Kathmandu each summer to contribute directly to the business. I have worked across ERP implementation, export strategy, and long-term manufacturing expansion, learning to hold the operational detail and the larger strategic picture at the same time.
                </p>
                <p>
                  The other half of my formation is less conventional. I trained in Hindustani classical music, grew up reading across three languages, translated a book, wrote for a national newspaper, and helped organise a literature festival. I do not think of these as separate from the business work. The same qualities that make a serious vocalist, sustained attention, responsiveness, and the willingness to be corrected, are the ones that make a serious operator.
                </p>
                <p>
                  This is where I am from. The work ahead is the next chapter of it.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal variant="scale" delay={200}>
            <p className="quote-text mt-12">
              "Ambition without culture is noise. Culture without discipline is decoration."
            </p>
          </Reveal>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="section-dark">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">Selected Achievements</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 achievements-grid">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <Reveal key={achievement.title} variant="fade-up" delay={index * STAGGER_ACHIEVEMENT_MS}>
                  <Card className="achievement-card">
                    {achievement.image && (
                      <div className={`achievement-image-wrapper ${achievement.imageFit === 'contain' ? 'achievement-image-contain' : ''}`}>
                        <img
                          src={achievement.image}
                          alt={achievement.title}
                          className="achievement-image"
                          style={{
                            ...(achievement.imagePosition ? { objectPosition: achievement.imagePosition } : {}),
                            ...(achievement.imageFit ? { objectFit: achievement.imageFit } : {})
                          }}
                        />
                      </div>
                    )}
                    <CardHeader>
                      {!achievement.image && (
                        <div className="achievement-icon">
                          <Icon className="w-6 h-6" />
                        </div>
                      )}
                      <CardTitle className="text-xl">{achievement.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        {achievement.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Reveal>);

            })}
          </div>
        </div>
      </section>

      {/* Fields of Obsession */}
      <section className="section-light">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">Domains</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {passions.map((passion, index) => {
              const Icon = passion.icon;
              return (
                <Reveal key={passion.title} variant="fade-up" delay={index * STAGGER_DOMAINS_MS}>
                  <div className="passion-card">
                    <Icon className="w-8 h-8 mb-4" />
                    <h3 className="passion-title">{passion.title}</h3>
                  </div>
                </Reveal>);

            })}
          </div>
        </div>
      </section>

      {/* Work & Projects */}
      <section id="work" className="section-dark">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">Work & Projects</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {projects.map((project, index) =>
            <Reveal key={project.title} variant="fade-up" delay={(index % 2) * STAGGER_PROJECT_MS}>
              <Card className="project-card" data-tilt>
                <ProjectMedia project={project} />
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                      <span className="project-type">{project.type}</span>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {project.link &&
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="external-link" title={project.link.endsWith('.pdf') ? 'View PDF' : 'Open link'}>
                          {project.link.endsWith('.pdf') ? <FileText className="w-5 h-5" /> : <ExternalLink className="w-5 h-5" />}
                        </a>
                    }
                      {project.secondaryLink &&
                    <a href={project.secondaryLink.url} target="_blank" rel="noopener noreferrer" className="external-link" title={project.secondaryLink.label}>
                          <ExternalLink className="w-5 h-5" />
                        </a>
                    }
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    {project.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Certifications & Awards */}
      <section className="section-light">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">Certifications & Awards</h2></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
            {/* Certifications Column */}
            <Reveal variant="fade-right" delay={100}>
              <div className="flex items-center gap-3 mb-8">
                <GraduationCap className="w-7 h-7 cert-icon" />
                <h3 className="cert-column-title">Certifications</h3>
              </div>
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <Reveal key={cert.title} variant="fade-up" delay={index * STAGGER_CERT_MS}>
                    {cert.link ? (
                      <button
                        type="button"
                        onClick={() => setActiveCert(cert)}
                        className="cert-item cert-item-link"
                      >
                        <div className="cert-item-content">
                          <p className="cert-title">{cert.title}</p>
                          <p className="cert-issuer">{cert.issuer}</p>
                        </div>
                        <span className="cert-view-cta" aria-hidden="true">
                          <span className="cert-view-text">View Certificate</span>
                          <span className="cert-view-icon-wrap">
                            <FileText className="w-4 h-4" />
                          </span>
                        </span>
                      </button>
                    ) : (
                      <div className="cert-item">
                        <p className="cert-title">{cert.title}</p>
                        <p className="cert-issuer">{cert.issuer}</p>
                      </div>
                    )}
                  </Reveal>
                ))}
              </div>
            </Reveal>

            {/* Awards Column */}
            <Reveal variant="fade-left" delay={200}>
              <div className="flex items-center gap-3 mb-8">
                <Trophy className="w-7 h-7 cert-icon" />
                <h3 className="cert-column-title">Awards</h3>
              </div>
              <div className="awards-feature-image-wrapper">
                <img
                  src="https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/6wmbu9tj_CAMT4864.JPG"
                  alt="Gentleman of the Year Award, Welham Boys' School"
                  className="awards-feature-image"
                />
                <p className="awards-feature-caption">Gentleman of the Year, Welham Boys' School, 2024.</p>
              </div>
              <div className="awards-feature-image-wrapper mt-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_strateg-jha/artifacts/wq1j824j_CAMT4842.JPG"
                  alt="Faculty Award for Exemplary Behaviour, Welham Boys' School"
                  className="awards-feature-image"
                  style={{ objectPosition: 'center 30%' }}
                />
                <p className="awards-feature-caption">Faculty Award for Exemplary Behaviour, Welham Boys' School, 2024.</p>
              </div>
              <div className="space-y-4 mt-6">
                {awards.map((award, index) => (
                  <Reveal key={`${award.title}-${award.issuer}`} variant="fade-up" delay={index * STAGGER_AWARDS_MS}>
                    <div className="cert-item">
                      <p className="cert-title">{award.title}</p>
                      <p className="cert-issuer">
                        {award.issuer}
                        {award.date && <span className="cert-date"> · {award.date}</span>}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="section-dark">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal variant="fade-up"><h2 className="section-title">Credentials</h2></Reveal>
          <div className="credentials-grid mt-12">
            <Reveal variant="fade-up" delay={0}>
              <div className="credential-item">
                <h3 className="credential-title">Education</h3>
                <p className="credential-text">Purdue University</p>
                <p className="credential-subtitle">B.S. Industrial Engineering Technology</p>
                <p className="credential-meta">West Lafayette, Indiana</p>
                <a
                  href="https://polytechnic.purdue.edu/profile/jha59"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="credential-profile-link"
                  data-testid="purdue-profile-link"
                >
                  Purdue Polytechnic Profile <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <div className="credential-divider" />
                <p className="credential-text">Welham Boys' School</p>
                <p className="credential-subtitle">Dehradun, India</p>
                <ul className="credential-roles">
                  <li>Music Captain · 2024</li>
                  <li>Editor-in-Chief, The Sankalp Magazine · 2024</li>
                  <li>Senior Editor, The Oliphant · 2024</li>
                  <li>Senior Editor, The Wavelength · 2024</li>
                </ul>
              </div>
            </Reveal>
            <Reveal variant="fade-up" delay={CREDENTIAL_STEP_MS}>
              <div className="credential-item">
                <h3 className="credential-title">Languages</h3>
                <p className="credential-text">English</p>
                <p className="credential-text">Nepali</p>
                <p className="credential-text">Hindi</p>
                <p className="credential-text">Maithili</p>
              </div>
            </Reveal>
            <Reveal variant="fade-up" delay={CREDENTIAL_STEP_MS * 2}>
              <div className="credential-item">
                <h3 className="credential-title">Professional Work</h3>
                <p className="credential-text">Nepovit Group — Operations</p>
                <p className="credential-subtitle">ERP implementation, export strategy, manufacturing expansion</p>
              </div>
            </Reveal>
            <Reveal variant="fade-up" delay={CREDENTIAL_STEP_MS * 3}>
              <div className="credential-item">
                <h3 className="credential-title">University Affiliations</h3>
                <p className="credential-text">Ascend Purdue</p>
                <p className="credential-text">ICMAP — Hindustani Music Coordinator</p>
                <p className="credential-text">Purdue Real Estate Society</p>
                <p className="credential-text">Purdue Marketing Association</p>
                <p className="credential-text">Financial Management Association at Purdue</p>
                <p className="credential-text">PurdueThink Consulting</p>
                <p className="credential-text">South Asian Society of Engineers</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Project Polymath Section */}
      <section id="polymath" className="polymath-section">
        <div className="max-w-7xl mx-auto px-6 py-32">
          {/* Header */}
          <Reveal variant="fade-up" className="polymath-header">
            <span className="polymath-eyebrow">A Personal Ideology</span>
            <h2 className="polymath-title">Project Polymath</h2>
            <p className="polymath-subtitle">
              A philosophy of modern manhood, where success, culture, discipline, and gentlemanship are cultivated together.
            </p>
          </Reveal>

          {/* Intro */}
          <Reveal variant="fade-up" delay={100} className="polymath-intro">
            <p>
              Project Polymath is my belief that a man should not be shaped by ambition alone. He should strive for excellence in his work, but also train his taste, deepen his cultural understanding, refine his manners, strengthen his body, and become a serious steward of character.
            </p>
            <p className="polymath-intro-emphasis">
              To be accomplished is admirable. To be accomplished and cultivated is rarer.
            </p>
          </Reveal>

          {/* Pillars */}
          <div className="polymath-pillars">
            {polymathPillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <Reveal key={pillar.title} variant="fade-up" delay={index * STAGGER_PILLAR_MS} className="polymath-pillar">
                  <div className="polymath-pillar-number">{String(index + 1).padStart(2, '0')}</div>
                  <Icon className="polymath-pillar-icon" />
                  <h3 className="polymath-pillar-title">{pillar.title}</h3>
                  <p className="polymath-pillar-description">{pillar.description}</p>
                </Reveal>
              );
            })}
          </div>

          {/* Featured Quote */}
          <Reveal variant="scale" className="polymath-quote-block">
            <span className="polymath-quote-mark">"</span>
            <p className="polymath-quote">
              The ideal man is not merely successful. He is cultivated.
            </p>
          </Reveal>

          {/* Manifesto + Moodboard split */}
          <div className="polymath-manifesto-grid">
            <Reveal variant="fade-right" className="polymath-manifesto">
              <span className="polymath-section-label">Manifesto</span>
              <div className="polymath-manifesto-lines">
                {polymathManifesto.map((line, index) => (
                  <Reveal key={line} variant="fade-up" delay={index * STAGGER_MANIFESTO_MS} className="polymath-manifesto-line">
                    <span className="polymath-manifesto-dot" />
                    <p>{line}</p>
                  </Reveal>
                ))}
              </div>
            </Reveal>
            <Reveal variant="fade-left" delay={100} className="polymath-moodboard">
              {polymathMoodboard.map((img, index) => (
                <div key={img.src} className="polymath-moodboard-item" data-parallax={index % 2 === 0 ? '0.05' : '-0.05'}>
                  <img src={img.src} alt={img.alt} />
                </div>
              ))}
            </Reveal>
          </div>

          {/* Closing line */}
          <Reveal variant="fade-up" className="polymath-closing">
            <p>
              Project Polymath is not about appearing sophisticated. It is about becoming difficult to reduce.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <Reveal variant="fade-up"><h2 className="section-title mb-6">Let's Stay In Touch</h2></Reveal>
          <Reveal variant="fade-up" delay={100}>
            <p className="contact-subtitle">
              Always open to a good conversation, whether about writing, music, ideas, or work.
            </p>
          </Reveal>
          <Reveal variant="fade-up" delay={200}>
            <div className="contact-grid">
              <a href="mailto:bimarsh045@gmail.com" className="contact-tile magnetic">
                <span className="contact-tile-icon"><Mail className="w-5 h-5" /></span>
                <span className="contact-tile-label">Email</span>
                <span className="contact-tile-value">bimarsh045@gmail.com</span>
              </a>
              <a href="https://www.linkedin.com/in/bimarsh-jha/" target="_blank" rel="noopener noreferrer" className="contact-tile magnetic">
                <span className="contact-tile-icon"><Linkedin className="w-5 h-5" /></span>
                <span className="contact-tile-label">LinkedIn</span>
                <span className="contact-tile-value">bimarsh-jha</span>
              </a>
              <a href="https://www.instagram.com/bimarsh_j/" target="_blank" rel="noopener noreferrer" className="contact-tile magnetic">
                <span className="contact-tile-icon"><Instagram className="w-5 h-5" /></span>
                <span className="contact-tile-label">Instagram</span>
                <span className="contact-tile-value">@bimarsh_j</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="footer-text">© 2025 Bimarsh Jha. All rights reserved.</p>
            <div className="flex gap-6 items-center flex-wrap">
              <a href="mailto:bimarsh045@gmail.com" className="footer-link">Email</a>
              <a href="https://www.linkedin.com/in/bimarsh-jha/" target="_blank" rel="noopener noreferrer" className="footer-link">LinkedIn</a>
              <a href="https://www.instagram.com/bimarsh_j/" target="_blank" rel="noopener noreferrer" className="footer-link">Instagram</a>
              <a href="https://polytechnic.purdue.edu/profile/jha59" target="_blank" rel="noopener noreferrer" className="footer-link" data-testid="footer-purdue-link">Purdue Profile</a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;